/* Uit Krabbelpaal.dc.html; bodies letterlijk overgenomen. */

import { GH, GW } from '../lib/config.js';
import { PALETTE_HEX, PALETTE_RGB, nearestPalette } from '../lib/palette.js';
import { playBlip, playPop } from '../lib/sound.js';

export default {
  clampG(v) { return Math.max(0, Math.min(GW - 1, v)); },

  clampGY(v) { return Math.max(0, Math.min(GH - 1, v)); },

  clearSel() { if (this.state.sel || this.state.lassoPts) this.setState({ sel: null, lassoPts: null }); },

  inSel(x, y) {
      const s = this.state.sel;
      if (!s || x < s.x || y < s.y || x >= s.x + s.w || y >= s.y + s.h) return false;
      if (!s.mask) return true;
      return !!s.mask[(y - s.y) * s.w + (x - s.x)];
    },

  // Lasso: vrije vorm. Elke cel waarvan het midden binnen de getrokken lus
  // valt, hoort bij de selectie; de lus zelf wordt altijd meegenomen.
  lassoToSel(pts) {
      if (!pts || pts.length < 3) return null;
      let minx = pts[0].x, maxx = pts[0].x, miny = pts[0].y, maxy = pts[0].y;
      pts.forEach((p) => {
        if (p.x < minx) minx = p.x; if (p.x > maxx) maxx = p.x;
        if (p.y < miny) miny = p.y; if (p.y > maxy) maxy = p.y;
      });
      const w = maxx - minx + 1, h = maxy - miny + 1;
      if (w < 2 && h < 2) return null;
      const mask = new Uint8Array(w * h);
      let count = 0;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const px = minx + x + 0.5, py = miny + y + 0.5;
          let inside = false;
          for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
            const xi = pts[i].x + 0.5, yi = pts[i].y + 0.5, xj = pts[j].x + 0.5, yj = pts[j].y + 0.5;
            if (((yi > py) !== (yj > py)) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
          }
          if (inside) { mask[y * w + x] = 1; count++; }
        }
      }
      pts.forEach((p) => {
        const i = (p.y - miny) * w + (p.x - minx);
        if (!mask[i]) { mask[i] = 1; count++; }
      });
      if (!count) return null;
      return { x: minx, y: miny, w, h, mask, pts: pts.slice() };
    },

  // Het kader is geen lus maar een blok: elke cel tussen de twee hoeken hoort
  // erbij, ook die op de rand zelf.
  rectToSel(pts) {
      if (!pts || !pts.length) return null;
      let minx = pts[0].x, maxx = pts[0].x, miny = pts[0].y, maxy = pts[0].y;
      pts.forEach((p) => {
        if (p.x < minx) minx = p.x; if (p.x > maxx) maxx = p.x;
        if (p.y < miny) miny = p.y; if (p.y > maxy) maxy = p.y;
      });
      const w = maxx - minx + 1, h = maxy - miny + 1;
      if (w < 1 || h < 1) return null;
      const mask = new Uint8Array(w * h).fill(1);
      return { x: minx, y: miny, w, h, mask, pts: null };
    },

  onSelDown(e) {
      e.preventDefault();
      const p = this.getPos(e);
      const x = this.clampG(p.x), y = this.clampGY(p.y);
      if (this.inSel(x, y)) {
        const s = this.state.sel;
        const ctx = this.getCtx();
        this.pushUndo();
        const lc = document.createElement('canvas');
        lc.width = s.w; lc.height = s.h;
        const lifted = ctx.getImageData(s.x, s.y, s.w, s.h);
        const hole = ctx.getImageData(s.x, s.y, s.w, s.h);
        // Alleen de pixels binnen de lus verhuizen; de rest blijft staan.
        for (let i = 0; i < s.w * s.h; i++) {
          const on = s.mask ? s.mask[i] : 1;
          if (on) { hole.data[i * 4 + 3] = 0; } else { lifted.data[i * 4 + 3] = 0; }
        }
        lc.getContext('2d').putImageData(lifted, 0, 0);
        this.liftCanvas = lc;
        ctx.putImageData(hole, s.x, s.y);
        this.moveBase = ctx.getImageData(0, 0, GW, GH);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(lc, s.x, s.y);
        this.moveFrom = { x, y, sx: s.x, sy: s.y };
        window.addEventListener('mousemove', this.onSelMoveWin);
        window.addEventListener('mouseup', this.onSelUpWin);
      } else {
        this.marqueeFrom = { x, y };
        window.addEventListener('mousemove', this.onMarqueeWin);
        window.addEventListener('mouseup', this.onMarqueeUpWin);
        this.setState({ sel: null, lassoPts: [{ x, y }] });
      }
    },

  onMarqueeWin(e) {
      if (!this.marqueeFrom || !this.canvasRef.current) return;
      const p = this.getPos(e);
      const x = this.clampG(p.x), y = this.clampGY(p.y);
      // Het kader hoeft maar twee hoeken te weten; de lasso onthoudt het hele pad.
      if (this.state.tool === 'marquee') {
        const a = this.marqueeFrom;
        this.setState({ lassoPts: [{ x: a.x, y: a.y }, { x, y: a.y }, { x, y }, { x: a.x, y }] });
        return;
      }
      this.setState((st) => {
        const pts = st.lassoPts || [];
        const last = pts[pts.length - 1];
        if (last && last.x === x && last.y === y) return null;
        return { lassoPts: pts.concat([{ x, y }]) };
      });
    },

  onMarqueeUpWin() {
      window.removeEventListener('mousemove', this.onMarqueeWin);
      window.removeEventListener('mouseup', this.onMarqueeUpWin);
      const pts = this.state.lassoPts;
      const sel = this.state.tool === 'marquee' ? this.rectToSel(pts) : this.lassoToSel(pts);
      this.setState({ lassoPts: null, sel: sel });
    },

  onSelMoveWin(e) {
      const f = this.moveFrom, s = this.state.sel;
      if (!f || !s || !this.canvasRef.current) return;
      const p = this.getPos(e);
      // Ruim buiten het doek mag: zo kun je een selectie helemaal weg schuiven.
      const nx = Math.max(-s.w, Math.min(GW, f.sx + (p.x - f.x)));
      const ny = Math.max(-s.h, Math.min(GH, f.sy + (p.y - f.y)));
      if (nx === s.x && ny === s.y) return;
      const ctx = this.getCtx();
      ctx.putImageData(this.moveBase, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(this.liftCanvas, nx, ny);
      const dx = nx - s.x, dy = ny - s.y;
      this.setState({
        sel: {
          x: nx, y: ny, w: s.w, h: s.h, mask: s.mask,
          pts: s.pts ? s.pts.map((p) => ({ x: p.x + dx, y: p.y + dy })) : null,
          fillEmpty: s.fillEmpty
        }
      });
    },

  // Dubbelklik met de lasso: alle pixels van dezelfde kleur op het hele doek
  // in één keer selecteren.
  onCanvasDblClick(e) {
      if (!this.isSelectTool(this.state.tool) || !this.canvasRef.current) return;
      e.preventDefault();
      const p = this.getPos(e);
      const x = this.clampG(p.x), y = this.clampGY(p.y);
      const ctx = this.getCtx();
      const img = ctx.getImageData(0, 0, GW, GH);
      const d = img.data;
      const at = (i) => [d[i * 4], d[i * 4 + 1], d[i * 4 + 2], d[i * 4 + 3]];
      const [tr, tg, tb, ta] = at(y * GW + x);
      const mask = new Uint8Array(GW * GH);
      let count = 0;
      for (let i = 0; i < GW * GH; i++) {
        const [r, g, b, a] = at(i);
        // Doorzichtige cellen horen bij elkaar, gekleurde moeten gelijk zijn.
        const same = ta === 0 ? a === 0 : (a > 0 && r === tr && g === tg && b === tb);
        if (same) { mask[i] = 1; count++; }
      }
      if (!count) return;
      playBlip();
      // Pakte je de leegte, dan mag een kleur daar juist wél in: zo vul je in
      // één klik alles wat nog doorzichtig is.
      this.setState({ lassoPts: null, sel: { x: 0, y: 0, w: GW, h: GH, mask, pts: null, fillEmpty: ta === 0 } });
    },

  // Het hele doek als selectie: daarna kleurt één kleurklik alles om.
  selectAll() {
      if (!this.canvasRef.current) return;
      this.finishPen();
      this.flashKey('select');
      const mask = new Uint8Array(GW * GH);
      mask.fill(1);
      this.setState({
        tool: this.isSelectTool(this.state.tool) ? this.state.tool : 'marquee',
        lassoPts: null,
        sel: { x: 0, y: 0, w: GW, h: GH, mask: mask, pts: null }
      });
    },

  // Spiegelen: de selectie als er een ligt, anders het hele beeld.
  flipArt(vert) {
      if (!this.canvasRef.current) return;
      const s = this.state.sel;
      this.pushUndo();
      const ctx = this.getCtx();
      ctx.imageSmoothingEnabled = false;
      if (s) {
        const lc = this.liftSel(s);
        const fc = document.createElement('canvas');
        fc.width = s.w; fc.height = s.h;
        const fx = fc.getContext('2d');
        fx.imageSmoothingEnabled = false;
        fx.translate(vert ? 0 : s.w, vert ? s.h : 0);
        fx.scale(vert ? 1 : -1, vert ? -1 : 1);
        fx.drawImage(lc, 0, 0);
        ctx.drawImage(fc, s.x, s.y);
        let mask = s.mask;
        if (mask) {
          const m2 = new Uint8Array(s.w * s.h);
          for (let y = 0; y < s.h; y++) {
            for (let x = 0; x < s.w; x++) {
              const sx = vert ? x : s.w - 1 - x;
              const sy = vert ? s.h - 1 - y : y;
              m2[y * s.w + x] = mask[sy * s.w + sx];
            }
          }
          mask = m2;
        }
        this.setState({ lassoPts: null, sel: { x: s.x, y: s.y, w: s.w, h: s.h, mask: mask, pts: null, fillEmpty: s.fillEmpty } });
      } else {
        const src = document.createElement('canvas');
        src.width = GW; src.height = GH;
        src.getContext('2d').drawImage(this.canvasRef.current, 0, 0);
        ctx.clearRect(0, 0, GW, GH);
        ctx.save();
        ctx.translate(vert ? 0 : GW, vert ? GH : 0);
        ctx.scale(vert ? 1 : -1, vert ? -1 : 1);
        ctx.drawImage(src, 0, 0);
        ctx.restore();
      }
      this.snapshotCanvas();
    },

  // X springt terug naar de kleur waarmee je hiervoor werkte.
  swapColor() {
      const p = this.prevIndex;
      if (p == null || p === this.state.primaryIndex) return;
      this.pickPaletteColor(p);
    },

  // Een kleur kiezen terwijl er een selectie ligt, kleurt die selectie om.
  pickPaletteColor(i) {
      if (i !== this.state.primaryIndex) this.prevIndex = this.state.primaryIndex;
      const s = this.state.sel;
      if (s && this.isSelectTool(this.state.tool) && this.canvasRef.current) {
        this.pushUndo();
        const ctx = this.getCtx();
        const img = ctx.getImageData(0, 0, GW, GH);
        const d = img.data;
        const hex = PALETTE_HEX[i];
        const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
        for (let cy = 0; cy < s.h; cy++) {
          for (let cx = 0; cx < s.w; cx++) {
            if (s.mask && !s.mask[cy * s.w + cx]) continue;
            const gx = s.x + cx, gy = s.y + cy;
            if (gx < 0 || gy < 0 || gx >= GW || gy >= GH) continue;
            const k = (gy * GW + gx) * 4;
            // Alleen wat al kleur heeft krijgt de nieuwe kleur, behalve als je de
            // leegte zelf hebt aangewezen.
            if (d[k + 3] === 0 && !s.fillEmpty) continue;
            d[k] = r; d[k + 1] = g; d[k + 2] = b; d[k + 3] = 255;
          }
        }
        ctx.putImageData(img, 0, 0);
        this.snapshotCanvas();
      }
      this.setState({ primaryIndex: i });
    },

  // De pixels van de selectie uit het doek tillen: wat je meeneemt en het gat
  // dat achterblijft.
  liftSel(s) {
      const ctx = this.getCtx();
      const lifted = ctx.getImageData(s.x, s.y, s.w, s.h);
      const hole = ctx.getImageData(s.x, s.y, s.w, s.h);
      for (let i = 0; i < s.w * s.h; i++) {
        const on = s.mask ? s.mask[i] : 1;
        if (on) hole.data[i * 4 + 3] = 0; else lifted.data[i * 4 + 3] = 0;
      }
      const lc = document.createElement('canvas');
      lc.width = s.w; lc.height = s.h;
      lc.getContext('2d').putImageData(lifted, 0, 0);
      ctx.putImageData(hole, s.x, s.y);
      return lc;
    },

  // Met de pijltjes precies één pixel opschuiven, met shift acht tegelijk.
  nudgeSel(dx, dy) {
      const s = this.state.sel;
      if (!s || !this.canvasRef.current) return;
      this.pushUndo();
      const lc = this.liftSel(s);
      const nx = Math.max(-s.w, Math.min(GW, s.x + dx));
      const ny = Math.max(-s.h, Math.min(GH, s.y + dy));
      const ctx = this.getCtx();
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(lc, nx, ny);
      const mx = nx - s.x, my = ny - s.y;
      this.setState({ sel: { x: nx, y: ny, w: s.w, h: s.h, mask: s.mask, pts: s.pts ? s.pts.map((p) => ({ x: p.x + mx, y: p.y + my })) : null } });
      this.snapshotCanvas();
    },

  // Alles binnen de lus weggummen.
  eraseSel(keep) {
      const s = this.state.sel;
      if (!s || !this.canvasRef.current) return;
      this.pushUndo();
      this.liftSel(s);
      if (keep) this.setState({ lassoPts: null, sel: { x: s.x, y: s.y, w: s.w, h: s.h, mask: s.mask, pts: s.pts, fillEmpty: true } });
      else this.clearSel();
      this.snapshotCanvas();
    },

  // Kopiëren en plakken met de lasso of het kader. Wat je plakt landt op
  // dezelfde plek als waar je het pakte, ook in een ander beeld.
  copySel() {
      const s = this.state.sel;
      if (!s || !this.canvasRef.current) return;
      const x0 = Math.max(0, s.x), y0 = Math.max(0, s.y);
      const w = Math.min(GW, s.x + s.w) - x0, h = Math.min(GH, s.y + s.h) - y0;
      if (w <= 0 || h <= 0) return;
      const img = this.getCtx().getImageData(x0, y0, w, h);
      let any = false;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const on = s.mask ? s.mask[(y + y0 - s.y) * s.w + (x + x0 - s.x)] : 1;
          const k = (y * w + x) * 4;
          if (!on) img.data[k + 3] = 0;
          else if (img.data[k + 3]) any = true;
        }
      }
      if (!any) return;
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').putImageData(img, 0, 0);
      this.clip = { canvas: c, w, h, x: x0, y: y0 };
      this.setState({ hasClip: true });
      playBlip();
    },

  cutSel() { if (!this.state.sel) return; this.copySel(); this.eraseSel(); },

  // Een plaatje uit het klembord: het krimpt tot het op de paal past, gaat door
  // het palet van acht kleuren en komt als los stukje op het canvas te liggen.
  onPasteImage(e) {
      clearTimeout(this.pasteFallbackTimer);
      if (!this.tekentNu() || this.state.dialog || this.state.chatOpen || this.state.viewerId) return;
      const items = (e.clipboardData && e.clipboardData.items) || [];
      let file = null;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type && items[i].type.indexOf('image/') === 0) { file = items[i].getAsFile(); break; }
      }
      if (!file) { if (this.clip) this.pasteClip(); return; }
      e.preventDefault();
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); this.placePastedImage(img); };
      img.onerror = () => URL.revokeObjectURL(url);
      img.src = url;
    },

  placePastedImage(img) {
      if (!img.width || !img.height || !this.canvasRef.current) return;
      const scale = Math.min(GW / img.width, GH / img.height);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const tmp = document.createElement('canvas');
      tmp.width = w; tmp.height = h;
      const tctx = tmp.getContext('2d');
      // Wél zacht schalen: dan middelt hij de pixels uit in plaats van er
      // willekeurige uit te pikken.
      tctx.imageSmoothingEnabled = true;
      tctx.imageSmoothingQuality = 'high';
      tctx.drawImage(img, 0, 0, w, h);
      const src = tctx.getImageData(0, 0, w, h).data;
      const out = document.createElement('canvas');
      out.width = w; out.height = h;
      const octx = out.getContext('2d');
      const dst = octx.createImageData(w, h);
      const o = dst.data;
      // Elke pixel de paletkleur die er het dichtst bij ligt, en verder niets. Er
      // heeft hier een raster gezeten om verlopen zachter te maken; dat werd op
      // tweeëndertig pixels breed textuur in plaats van tint en is er weer uit.
      for (let i = 0; i < w * h; i++) {
        if (src[i * 4 + 3] < 128) { o[i * 4 + 3] = 0; continue; }
        const p = PALETTE_RGB[nearestPalette(src[i * 4], src[i * 4 + 1], src[i * 4 + 2])];
        o[i * 4] = p[0]; o[i * 4 + 1] = p[1]; o[i * 4 + 2] = p[2]; o[i * 4 + 3] = 255;
      }
      octx.putImageData(dst, 0, 0);
      this.clip = { canvas: out, w, h, x: Math.floor((GW - w) / 2), y: Math.floor((GH - h) / 2) };
      this.setState({ hasClip: true }, this.pasteClip);
    },

  pasteClip() {
      const clip = this.clip;
      if (!clip || !this.canvasRef.current) return;
      this.pushUndo();
      const x = Math.max(0, Math.min(GW - clip.w, clip.x));
      const y = Math.max(0, Math.min(GH - clip.h, clip.y));
      const ctx = this.getCtx();
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(clip.canvas, x, y);
      // Het geplakte stukje blijft los liggen: meteen verslepen mag.
      const d = clip.canvas.getContext('2d').getImageData(0, 0, clip.w, clip.h).data;
      const mask = new Uint8Array(clip.w * clip.h);
      for (let i = 0; i < mask.length; i++) mask[i] = d[i * 4 + 3] ? 1 : 0;
      this.setState({
        tool: this.isSelectTool(this.state.tool) ? this.state.tool : 'marquee',
        sel: { x, y, w: clip.w, h: clip.h, mask, pts: null },
        lassoPts: null
      });
      this.snapshotCanvas();
      playPop();
    },

  onSelUpWin() {
      window.removeEventListener('mousemove', this.onSelMoveWin);
      window.removeEventListener('mouseup', this.onSelUpWin);
      this.liftCanvas = null;
      this.moveBase = null;
      this.moveFrom = null;
      this.snapshotCanvas();
    }
};
